import { addOutline, createOutline, listOutline } from 'ionicons/icons';
import type { ValueFormatterParams } from 'ag-grid-community';
import { ActionGridActionColumn, ActionGridBulkAction, ActionGridColumnDefinition } from '@/types/model/actionGrid';
import { InspectionGridRow } from '@/types/model/inspection';
import { getStatusText, getTimeSensitivityText } from '@/components/inspection/common/helpers';

const formatDate = (params: ValueFormatterParams): string => (
  params.value ? new Date(params.value as string).toLocaleDateString() : '—'
);

export const buildInspectionActionGridColumns = (): ActionGridColumnDefinition[] => [
  { key: 'inspectionResultId', headerName: 'ID', isRowId: true, colDef: { width: 90, filter: 'agNumberColumnFilter' } },
  { key: 'groupTitle', headerName: 'Group', colDef: { width: 160, filter: true } },
  { key: 'lineItem', headerName: 'Line Item', colDef: { width: 110, filter: true } },
  { key: 'title', headerName: 'Line Item Title', colDef: { flex: 1, minWidth: 160, filter: true, tooltipField: 'title' } },
  { key: 'siteName', headerName: 'Site', colDef: { width: 140, filter: true } },
  { key: 'inspectorName', headerName: 'Inspector', colDef: { width: 140, filter: true } },
  { key: 'scheduledDate', headerName: 'Scheduled On', colDef: { width: 120, filter: 'agDateColumnFilter', valueFormatter: formatDate } },
  { key: 'inspectedOn', headerName: 'Inspected On', colDef: { width: 120, filter: 'agDateColumnFilter', valueFormatter: formatDate } },
  { key: 'statusName', headerName: 'Status', colDef: { width: 130, filter: true } },
  { key: 'timeSensitivityName', headerName: 'Time Sensitivity', colDef: { width: 140, filter: true } },
  { key: 'compositeScore', headerName: 'Score', colDef: { width: 100, filter: 'agNumberColumnFilter' } },
  { key: 'isExceptional', headerName: 'Exceptional', colDef: { width: 110, filter: true } },
  { key: 'areaOfExpertise', headerName: 'Area of Expertise', colDef: { width: 150, filter: true } },
  { key: 'checklistItemCount', headerName: 'Checklist Items', colDef: { width: 130, filter: 'agNumberColumnFilter' } },
  { key: 'correctiveActionCount', headerName: 'Corrective Actions', colDef: { width: 140, filter: 'agNumberColumnFilter' } },
];

export const buildInspectionActionGridData = (rows: InspectionGridRow[]): unknown[][] => {
  const columns = buildInspectionActionGridColumns();
  const enrichedRows = rows.map((row) => ({
    ...row,
    statusName: getStatusText(row.status),
    timeSensitivityName: getTimeSensitivityText(row.timeSensitivity),
    checklistItemCount: row.checklistItems?.length ?? 0,
    correctiveActionCount: row.correctiveActions?.length ?? 0,
  }));

  return enrichedRows.map((row) => columns.map((column) => (row as unknown as Record<string, unknown>)[column.key]));
};

interface BuildInspectionActionGridBulkActionsParams {
  canAddNew: boolean;
  onAddNew: () => void;
  onSwitchToAccordion: () => void;
}

export const buildInspectionActionGridBulkActions = ({
  canAddNew,
  onAddNew,
  onSwitchToAccordion,
}: BuildInspectionActionGridBulkActionsParams): ActionGridBulkAction[] => {
  const actions: ActionGridBulkAction[] = [];

  if (canAddNew) {
    actions.push({
      id: 'new-inspection',
      label: 'New Inspection',
      icon: addOutline,
      onAction: () => onAddNew(),
    });
  }

  actions.push({
    id: 'switch-to-accordion-view',
    label: 'Switch to Accordion View',
    icon: listOutline,
    onAction: () => onSwitchToAccordion(),
  });

  return actions;
};

interface BuildInspectionActionGridActionColumnsParams {
  rows: InspectionGridRow[];
  onEdit: (row: InspectionGridRow) => void;
}

export const buildInspectionActionGridActionColumns = ({
  rows,
  onEdit,
}: BuildInspectionActionGridActionColumnsParams): ActionGridActionColumn[] => {
  const rowsById = new Map(rows.map((row) => [String(row.inspectionResultId), row]));

  return [
    {
      id: 'inspection-row',
      headerName: 'Actions',
      width: 70,
      pinned: 'right',
      actions: [
        {
          id: 'view-inspection',
          title: 'View Inspection',
          icon: createOutline,
          onAction: ({ row }) => {
            const found = rowsById.get(row.rowId);
            if (found) {
              onEdit(found);
            }
          },
        },
      ],
    },
  ];
};
